import type { GuestDto } from '@workshop-hst/shared';

/**
 * Inline guest row used on cards: small avatar + name + compact company attribution.
 * Wide uploaded wordmarks can replace the logo-chip + company-name text.
 */
export function GuestRow({ guest, size = 'md' }: { guest: GuestDto; size?: 'sm' | 'md' | 'lg' }) {
  const avatarSize = size === 'lg' ? 36 : size === 'md' ? 26 : 20;
  const logoChip = size === 'lg' ? 16 : size === 'md' ? 12 : 10;
  const wordmarkHeight = size === 'lg' ? 18 : size === 'md' ? 15 : 13;
  const wordmarkMaxWidth = size === 'lg' ? 132 : size === 'md' ? 110 : 92;
  const hasHeadshot = Boolean(guest.headshotUrl);
  const useWordmark = Boolean(guest.companyLogoUrl && guest.companyLogoIncludesName);

  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <Avatar guest={guest} size={avatarSize} />
      <span className="flex flex-col min-w-0 leading-tight">
        <span
          className={
            size === 'lg' ? 'text-sm font-semibold truncate' : 'text-xs font-medium truncate'
          }
        >
          {guest.name}
        </span>
        <span
          className={
            size === 'lg'
              ? 'flex items-center gap-1 text-xs text-ink-subtle min-w-0'
              : 'flex items-center gap-1 text-[10px] text-ink-subtle min-w-0'
          }
        >
          {useWordmark ? (
            <img
              src={guest.companyLogoUrl ?? ''}
              alt={guest.companyName}
              className="min-w-0 object-contain object-left"
              style={{ height: wordmarkHeight, maxWidth: wordmarkMaxWidth }}
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          ) : (
            <>
              {/* When we have a headshot, also show a tiny company-logo chip next to the company name */}
              {hasHeadshot && guest.companyLogoUrl ? (
                <img
                  src={guest.companyLogoUrl}
                  alt=""
                  className="rounded-sm bg-canvas object-contain"
                  style={{ width: logoChip, height: logoChip }}
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                />
              ) : null}
              <span className="truncate">{guest.companyName}</span>
            </>
          )}
        </span>
      </span>
    </span>
  );
}

function Avatar({ guest, size }: { guest: GuestDto; size: number }) {
  if (guest.headshotUrl) {
    return (
      <img
        src={guest.headshotUrl}
        alt=""
        className="shrink-0 rounded-full bg-canvas object-cover ring-1 ring-line"
        style={{ width: size, height: size }}
      />
    );
  }
  if (guest.companyLogoUrl && !guest.companyLogoIncludesName) {
    return (
      <span
        className="shrink-0 inline-flex items-center justify-center overflow-hidden rounded-full bg-canvas"
        style={{ width: size, height: size }}
      >
        <img
          src={guest.companyLogoUrl}
          alt=""
          className="h-[80%] w-[80%] object-contain"
          onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
        />
      </span>
    );
  }
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full bg-canvas font-bold text-ink-subtle"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {guest.companyName[0]?.toUpperCase()}
    </span>
  );
}

/**
 * Larger guest card used on the detail page "With guest" section.
 */
export function GuestCard({ guest }: { guest: GuestDto }) {
  const useWordmark = Boolean(guest.companyLogoUrl && guest.companyLogoIncludesName);
  return (
    <div className="flex items-center gap-3">
      <Avatar guest={guest} size={48} />
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-sm font-semibold truncate">{guest.name}</span>
        <span className="flex items-center gap-1.5 text-xs text-ink-muted">
          {useWordmark ? (
            <img
              src={guest.companyLogoUrl ?? ''}
              alt={guest.companyName}
              className="min-w-0 object-contain object-left"
              style={{ height: 20, maxWidth: 160 }}
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          ) : (
            <>
              {guest.companyLogoUrl ? (
                <img
                  src={guest.companyLogoUrl}
                  alt=""
                  className="h-3.5 w-3.5 rounded-sm object-contain"
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                />
              ) : null}
              <span className="truncate">{guest.companyName}</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}
